//#region Modules
import {
  IUserSettingsRecord,
  UserSettingsRecordDateFormat,
  UserSettingsRecordTimeFormat,
  UserSettingsTheme,
  MultiColumnLayout,
  DefaultViewType,
} from "@anydo/interfaces";
import {
  DEFAULT_USER_SETTINGS_SELECTED_IMAGE,
  DEFAULT_ADDED_FIRST_MY_DAY_ENTRY,
  DEFAULT_IS_STICKY_VIDEO_DISMISSED,
  DEFAULT_MULTICOLUMN_ENTITY_TYPE,
  DEFAULT_MAC_STORE_LAST_INVOCATION_TIMESTAMP,
} from "../config/constants";
import { RecordUtils } from "./-private/RecordUtils";
//#endregion

//#region Default properties
const getDefaultProperty = RecordUtils.createGetDefaultProperty<IUserSettingsRecord>({
  dateFormat: null,
  selectedImage: DEFAULT_USER_SETTINGS_SELECTED_IMAGE,
  selectedImageDark: DEFAULT_USER_SETTINGS_SELECTED_IMAGE,
  theme: UserSettingsTheme.AUTO,
  language: "en",
  timeFormat: null,
  googleCalendarDisabledCalendars: "",
  addedFirstMyDayEntry: DEFAULT_ADDED_FIRST_MY_DAY_ENTRY,
  macStoreRateUsLastInvocationTimestamp: DEFAULT_MAC_STORE_LAST_INVOCATION_TIMESTAMP,
  isStickyMyDayVideoDismissed: DEFAULT_IS_STICKY_VIDEO_DISMISSED,
  isStickyBoardVideoDismissed: DEFAULT_IS_STICKY_VIDEO_DISMISSED,
  isStickyCardVideoDismissed: DEFAULT_IS_STICKY_VIDEO_DISMISSED,
  isStickyCopyInviteLinkDismissed: false,
  isStickyConnectWhatsAppTeamsDismissed: false,
  isCreateWorkspaceBannerDismissed: false,
  isScheduleOnboardingSessionDismissed: false,
  isOnboardingTourDismissed: false,
  hasSeenCustomFieldsTutorial: false,
  allMyTasksEntityType: DEFAULT_MULTICOLUMN_ENTITY_TYPE,
  nextSevenDaysEntityType: DEFAULT_MULTICOLUMN_ENTITY_TYPE,
  showMulticolumnOverdue: true,
  showMulticolumnChecked: true,
  multicolumnLayout: "default",
  personalAiFeaturesConsent: false,
  defaultView: "lastviewed",
  defaultViewLastViewed: "myday",
  cardActivityChatOnly: true,
  showWeekend: true,
  fcmTokens: [],
  collapsedSidebarSections: {},
  isBoardAutomationsWelcomeScreenDismissed: false,
  dismissedSmartCards: [],
});
//#endregion

export class UserSettingsRecord implements IUserSettingsRecord {
  static create(props?: Partial<IUserSettingsRecord>): IUserSettingsRecord {
    return new UserSettingsRecord(props);
  }

  dateFormat: UserSettingsRecordDateFormat | null;
  selectedImage: string;
  selectedImageDark: string;
  theme: UserSettingsTheme;
  language: string;
  timeFormat: UserSettingsRecordTimeFormat | null;
  googleCalendarDisabledCalendars: string;
  addedFirstMyDayEntry: boolean;
  macStoreRateUsLastInvocationTimestamp: number;
  isStickyMyDayVideoDismissed: boolean;
  isStickyBoardVideoDismissed: boolean;
  isStickyCardVideoDismissed: boolean;
  isStickyCopyInviteLinkDismissed: boolean;
  isStickyConnectWhatsAppTeamsDismissed: boolean;
  isCreateWorkspaceBannerDismissed: boolean;
  isScheduleOnboardingSessionDismissed: boolean;
  isOnboardingTourDismissed: boolean;
  hasSeenCustomFieldsTutorial: boolean;
  allMyTasksEntityType: "task" | "card";
  nextSevenDaysEntityType: "task" | "card";
  showMulticolumnOverdue: boolean;
  showMulticolumnChecked: boolean;
  multicolumnLayout: MultiColumnLayout;
  personalAiFeaturesConsent: boolean;
  defaultView: DefaultViewType;
  defaultViewLastViewed: Exclude<DefaultViewType, "lastviewed">;
  cardActivityChatOnly: boolean;
  showWeekend: boolean;
  fcmTokens: readonly string[];
  collapsedSidebarSections: Record<string, boolean>;
  isBoardAutomationsWelcomeScreenDismissed: boolean;
  dismissedSmartCards: readonly string[];

  private constructor(props?: Partial<IUserSettingsRecord>) {
    this.dateFormat = getDefaultProperty("dateFormat", props);
    this.selectedImage = getDefaultProperty("selectedImage", props);
    this.selectedImageDark = getDefaultProperty("selectedImageDark", props);
    this.theme = getDefaultProperty("theme", props);
    this.language = getDefaultProperty("language", props);
    this.timeFormat = getDefaultProperty("timeFormat", props);
    this.googleCalendarDisabledCalendars = getDefaultProperty("googleCalendarDisabledCalendars", props);
    this.addedFirstMyDayEntry = getDefaultProperty("addedFirstMyDayEntry", props);
    this.macStoreRateUsLastInvocationTimestamp = getDefaultProperty("macStoreRateUsLastInvocationTimestamp", props);
    this.isStickyMyDayVideoDismissed = getDefaultProperty("isStickyMyDayVideoDismissed", props);
    this.isStickyBoardVideoDismissed = getDefaultProperty("isStickyBoardVideoDismissed", props);
    this.isStickyCardVideoDismissed = getDefaultProperty("isStickyCardVideoDismissed", props);
    this.isStickyCopyInviteLinkDismissed = getDefaultProperty("isStickyCopyInviteLinkDismissed", props);
    this.isStickyConnectWhatsAppTeamsDismissed = getDefaultProperty("isStickyConnectWhatsAppTeamsDismissed", props);
    this.isCreateWorkspaceBannerDismissed = getDefaultProperty("isCreateWorkspaceBannerDismissed", props);
    this.isScheduleOnboardingSessionDismissed = getDefaultProperty("isScheduleOnboardingSessionDismissed", props);
    this.isOnboardingTourDismissed = getDefaultProperty("isOnboardingTourDismissed", props);
    this.hasSeenCustomFieldsTutorial = getDefaultProperty("hasSeenCustomFieldsTutorial", props);
    this.allMyTasksEntityType = getDefaultProperty("allMyTasksEntityType", props);
    this.nextSevenDaysEntityType = getDefaultProperty("nextSevenDaysEntityType", props);
    this.showMulticolumnOverdue = getDefaultProperty("showMulticolumnOverdue", props);
    this.showMulticolumnChecked = getDefaultProperty("showMulticolumnChecked", props);
    this.multicolumnLayout = getDefaultProperty("multicolumnLayout", props);
    this.personalAiFeaturesConsent = getDefaultProperty("personalAiFeaturesConsent", props);
    this.defaultView = getDefaultProperty("defaultView", props);
    this.defaultViewLastViewed = getDefaultProperty("defaultViewLastViewed", props);
    this.cardActivityChatOnly = getDefaultProperty("cardActivityChatOnly", props);
    this.showWeekend = getDefaultProperty("showWeekend", props);
    this.fcmTokens = getDefaultProperty("fcmTokens", props);
    this.collapsedSidebarSections = getDefaultProperty("collapsedSidebarSections", props);
    this.isBoardAutomationsWelcomeScreenDismissed = getDefaultProperty(
      "isBoardAutomationsWelcomeScreenDismissed",
      props,
    );
    this.dismissedSmartCards = getDefaultProperty("dismissedSmartCards", props);
  }
}
